import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Badge,
  Modal,
  TextInput,
  Textarea,
  Select,
  Label,
  Spinner,
} from 'flowbite-react';
import {
  HiPlus,
  HiTrash,
  HiExclamation,
  HiClipboardCopy,
  HiRefresh,
  HiPencil,
} from 'react-icons/hi';
import { observer } from 'mobx-react-lite';
import { apiKeyApi } from '../services/apiKeyApi';
import { toastStore } from '../stores/toastStore';
import {
  APIKey,
  CreateAPIKeyRequest,
  CreateAPIKeyResponse,
  APIKeyType,
} from '../types';

const APIKeyManager: React.FC = observer(() => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
  });
  const [newKeyResponse, setNewKeyResponse] = useState<CreateAPIKeyResponse | null>(null);
  const [filterType, setFilterType] = useState<APIKeyType | ''>('');

  const [formData, setFormData] = useState<CreateAPIKeyRequest>({
    name: '',
    description: '',
    type: 'iot',
    rateLimit: 60,
  });

  useEffect(() => {
    loadAPIKeys();
  }, [filterType]);

  const loadAPIKeys = async () => {
    setLoading(true);
    try {
      const keys = await apiKeyApi.listAPIKeys(filterType || undefined);
      setApiKeys(keys);
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAPIKey = async () => {
    try {
      const response = await apiKeyApi.createAPIKey(formData);
      setNewKeyResponse(response);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setFormData({
        name: '',
        description: '',
        type: 'iot',
        rateLimit: 60,
      });
      toastStore.showSuccess('APIキーを作成しました');
      loadAPIKeys();
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキーの作成に失敗しました');
    }
  };

  const handleRevokeKey = async (key: APIKey) => {
    try {
      await apiKeyApi.revokeAPIKey(key.id);
      toastStore.showSuccess('APIキーを無効化しました');
      loadAPIKeys();
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキーの無効化に失敗しました');
    }
  };

  const handleActivateKey = async (key: APIKey) => {
    try {
      await apiKeyApi.activateAPIKey(key.id);
      toastStore.showSuccess('APIキーを有効化しました');
      loadAPIKeys();
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキーの有効化に失敗しました');
    }
  };

  const handleEditKey = async () => {
    if (!selectedKey) return;

    try {
      await apiKeyApi.updateAPIKey(selectedKey.id, {
        name: editFormData.name,
        description: editFormData.description,
      });
      toastStore.showSuccess('APIキー情報を更新しました');
      setShowEditModal(false);
      loadAPIKeys();
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキー情報の更新に失敗しました');
    }
  };

  const openEditModal = (key: APIKey) => {
    setSelectedKey(key);
    setEditFormData({
      name: key.name,
      description: key.description || '',
    });
    setShowEditModal(true);
  };

  const handleDeleteKey = async () => {
    if (!selectedKey) return;

    try {
      await apiKeyApi.deleteAPIKey(selectedKey.id);
      toastStore.showSuccess('APIキーを削除しました');
      setShowDeleteModal(false);
      setSelectedKey(null);
      loadAPIKeys();
    } catch (err: any) {
      toastStore.showError(err.message || 'APIキーの削除に失敗しました');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toastStore.showSuccess('クリップボードにコピーしました');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold">APIキー管理</h3>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as APIKeyType | '')}
            >
              <option value="">すべて</option>
              <option value="iot">IoTデバイス</option>
              <option value="webhook">Webhook</option>
              <option value="admin">管理者</option>
            </Select>
          </div>
          <div className="flex space-x-2">
            <Button onClick={loadAPIKeys} size="sm" color="light">
              <HiRefresh className="mr-2 h-4 w-4" />
              更新
            </Button>
            <Button onClick={() => setShowCreateModal(true)} size="sm">
              <HiPlus className="mr-2 h-4 w-4" />
              新規作成
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="xl" />
          </div>
        ) : (
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>名前</Table.HeadCell>
              <Table.HeadCell>タイプ</Table.HeadCell>
              <Table.HeadCell>キー</Table.HeadCell>
              <Table.HeadCell>ステータス</Table.HeadCell>
              <Table.HeadCell>最終使用</Table.HeadCell>
              <Table.HeadCell>作成日</Table.HeadCell>
              <Table.HeadCell>アクション</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {apiKeys.map((key) => (
                <Table.Row key={key.id} className="bg-white">
                  <Table.Cell className="font-medium">
                    {key.name}
                    {key.description && (
                      <p className="text-xs text-gray-500">{key.description}</p>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={apiKeyApi.getTypeColor(key.type)}>
                      {key.type.toUpperCase()}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {apiKeyApi.getMaskedKey(key)}
                    </code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={apiKeyApi.getStatusColor(key)}>
                      {key.status === 'active' ? '有効' :
                       key.status === 'revoked' ? '無効' : '期限切れ'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {key.lastUsedAt ? formatDate(key.lastUsedAt) : '未使用'}
                  </Table.Cell>
                  <Table.Cell>{formatDate(key.createdAt)}</Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEditModal(key)}
                      >
                        <HiPencil className="h-3 w-3" />
                      </Button>
                      {key.status === 'active' && (
                        <Button
                          size="xs"
                          color="warning"
                          onClick={() => handleRevokeKey(key)}
                        >
                          無効化
                        </Button>
                      )}
                      {key.status === 'revoked' && (
                        <Button
                          size="xs"
                          color="success"
                          onClick={() => handleActivateKey(key)}
                        >
                          有効化
                        </Button>
                      )}
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => {
                          setSelectedKey(key);
                          setShowDeleteModal(true);
                        }}
                      >
                        <HiTrash className="h-3 w-3" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {apiKeys.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={7} className="text-center py-8 text-gray-500">
                    APIキーがありません
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        )}
      </Card>

      {/* Create API Key Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <Modal.Header>新規APIキー作成</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" value="名前 *" />
              <TextInput
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="例: IoTデバイス用キー"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" value="説明" />
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="このAPIキーの用途を説明"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="rateLimit" value="レート制限 (リクエスト/分)" />
              <TextInput
                id="rateLimit"
                type="number"
                value={formData.rateLimit || 60}
                onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
                placeholder="60"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCreateAPIKey}>作成</Button>
          <Button color="gray" onClick={() => setShowCreateModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New API Key Display Modal */}
      <Modal show={showKeyModal} onClose={() => setShowKeyModal(false)}>
        <Modal.Header>APIキーが作成されました</Modal.Header>
        <Modal.Body>
          <div className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 border border-yellow-300 flex items-start">
            <HiExclamation className="h-5 w-5 mr-2 flex-shrink-0" />
            <span>このAPIキーは一度だけ表示されます。必ず安全な場所に保管してください。</span>
          </div>

          {newKeyResponse && (
            <div className="mt-4 space-y-4">
              <div>
                <Label value="APIキー" />
                <div className="flex items-center space-x-2">
                  <code className="flex-1 bg-gray-100 p-3 rounded text-sm break-all">
                    {newKeyResponse.plainKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(newKeyResponse.plainKey)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <HiClipboardCopy className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div>
                <Label value="使用例" />
                <div className="bg-gray-900 text-white p-3 rounded text-sm">
                  <code>
                    curl -H "X-API-Key: {newKeyResponse.plainKey}" \<br />
                    &nbsp;&nbsp;-H "Content-Type: image/jpeg" \<br />
                    &nbsp;&nbsp;--data-binary @path/to/file \<br />
                    &nbsp;&nbsp;https://{location.host.replace("admin.", "api.")}/v1/iot/screens/capture
                  </code>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => {
            setShowKeyModal(false);
            setNewKeyResponse(null);
          }}>
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <Modal.Header>APIキーの削除</Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <HiExclamation className="mx-auto mb-4 h-14 w-14 text-gray-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-500">
              APIキー「{selectedKey?.name}」を削除しますか？<br />
              この操作は取り消せません。
            </h3>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={handleDeleteKey}>
            削除する
          </Button>
          <Button color="gray" onClick={() => setShowDeleteModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit API Key Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)}>
        <Modal.Header>APIキー情報の編集</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name" value="名前 *" />
              <TextInput
                id="edit-name"
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                placeholder="例: IoTデバイス用キー"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-description" value="説明" />
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="このAPIキーの用途を説明"
                rows={3}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleEditKey}>更新</Button>
          <Button color="gray" onClick={() => setShowEditModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default APIKeyManager;